import React, { useState } from 'react';
import { ChatBot } from './ChatBot';
import { ChatBotConfig } from '../types';
import { Box, IconButton, Fade, Zoom, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import ChatIcon from '@material-ui/icons/Chat';
import AssistantIcon from '@material-ui/icons/Assistant';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: theme.palette.background.default,
    position: 'relative',
  },
  floatingButton: {
    position: 'fixed',
    right: '11px',
    top: '50%',
    transform: 'translateY(-50%) rotate(90deg)',
    transformOrigin: 'right center',
    background: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    boxShadow: '-2px 4px 20px rgba(0, 0, 0, 0.15), -1px 2px 8px rgba(0, 0, 0, 0.1)',
    // transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    zIndex: 1000,
    padding: theme.spacing(1.5, 4),
    borderRadius: '12px 12px 0 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 'auto',
    border: 'none',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      right: '16px',
      background: theme.palette.primary.dark,
      boxShadow: '-4px 6px 28px rgba(0, 0, 0, 0.2), -2px 3px 12px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-50%) rotate(90deg) scale(1.05)',
    },
    '&:active': {
      transform: 'translateY(-50%) rotate(90deg) scale(0.98)',
    },
  },
  chatWidget: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '800px',
    height: '600px',
    zIndex: 999,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    background: '#fff',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '@media (max-width: 600px)': {
      width: 'calc(100vw - 32px)',
      height: 'calc(100vh - 100px)',
      bottom: '16px',
      right: '16px',
    },
  },
  chatWidgetExpanded: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: '1200px',
    height: '90vh',
    maxHeight: '800px',
    borderRadius: '16px',
  },
  chatHeader: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  chatHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  chatIcon: {
    fontSize: '24px',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  headerButton: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    padding: '6px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)',
    },
  },
  chatBotContainer: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export interface ChatBotWidgetProps {
  /**
   * Configuration for the chat bot
   */
  config?: ChatBotConfig;

  /**
   * Whether the chat widget should be open by default
   * @default false
   */
  defaultOpen?: boolean;

  /**
   * Whether the chat widget should be expanded by default
   * @default false
   */
  defaultExpanded?: boolean;

  /**
   * Custom title for the chat header
   * @default "AI Assistant"
   */
  headerTitle?: string;

  /**
   * Custom subtitle for the chat header (e.g., "Online", "Available")
   * @default "Online"
   */
  headerSubtitle?: string;

  /**
   * Whether to show the root container with background
   * Set to false if you want to use the widget without the background
   * @default true
   */
  showRootContainer?: boolean;
}

export const ChatBotWidget: React.FC<ChatBotWidgetProps> = observer(({
  config = {
    placeholder: 'Type your message here...',
    botName: 'AI Assistant',
    baseURL: '',
  },
  defaultOpen = false,
  defaultExpanded = false,
  headerTitle = 'AI Assistant',
  headerSubtitle = 'Online',
  showRootContainer = true,
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setIsExpanded(false);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const widgetContent = (
    <>
      {!isOpen && (
        <Fade in={!isOpen}>
          <Box
            className={classes.floatingButton}
            onClick={toggleChat}
            component="button"
            aria-label="Open AI Assistant"
          >
            <AssistantIcon style={{ fontSize: '20px' }} />
            <Typography style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
              AI Assistant
            </Typography>
          </Box>
        </Fade>
      )}

      {isOpen && (
        <Fade in={isOpen}>
          <Box className={`${classes.chatWidget} ${isExpanded ? classes.chatWidgetExpanded : ''}`}>
            <Box className={classes.chatHeader}>
              <Box className={classes.chatHeaderTitle}>
                <ChatIcon className={classes.chatIcon} />
                <Box>
                  <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 600 }}>
                    {headerTitle}
                  </Typography>
                  <Typography variant="caption" style={{ fontSize: '12px', opacity: 0.9 }}>
                    {headerSubtitle}
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.headerActions}>
                <IconButton
                  className={classes.headerButton}
                  onClick={toggleExpand}
                  size="small"
                  aria-label={isExpanded ? "Minimize chat" : "Expand chat"}
                  title={isExpanded ? "Minimize" : "Expand"}
                >
                  {isExpanded ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
                <IconButton
                  className={classes.headerButton}
                  onClick={toggleChat}
                  size="small"
                  aria-label="Close chat"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box className={classes.chatBotContainer}>
              <ChatBot />
            </Box>
          </Box>
        </Fade>
      )}
    </>
  );

  if (showRootContainer) {
    return <Box className={classes.root}>{widgetContent}</Box>;
  }

  return widgetContent;
});
